import {observer} from 'mobx-react-lite';
import styled from 'styled-components';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faCheck,
} from '@fortawesome/pro-solid-svg-icons';
import {getSingleUrlParam, getSiteName} from '@/utils/url';
import {Button} from '@/components/common-components/v2';
import React, {useEffect, useRef, useState} from 'react';
import {
  Modal, Tabs,
  Tooltip,
  Form,
} from 'antd';
import {useStore} from '@/store/root-store';
import {BusinessInfoForm} from './KnowledgeGraph/businessInfoForm';
import TopPages from './topPages';
import {customapiError} from '@/utils/api';
import {notification} from '@/utils/notification';
import {toJS} from 'mobx';
import ImagesForm from './imagesForm';
import {RingLoaderV2} from '@/components/common-components/components/skeleton/ring-loader-v2';
import {snakeToCamel} from '@/utils/string';
import {faBoxArchive} from '@fortawesome/pro-duotone-svg-icons';
import FreezeWrapper from './freezTooltip';

interface Props {
  isCompleted?: boolean;
}


export const OttoDetailKnowledgeGraph:React.FC<Props> = observer(() => {
  const {
    ottoV2Store: {getOttoV2Project, setDefaultParams, updateOttoV2Project, loadOttoV2Project, getKnowledgeGraphFieldsMeta, getKnowledgeGraphData, knowledgeGraph, updateKnowledgeGraph, getOttoQuotaAllocations, ottoGBPUploadImage, getOttoPlanQuotaAllocations,
      isKnowledgeModalVisible, setIsKnowledgeModalVisible, defaultParams, getOttoKnowledgeGraphFieldsMeta, getGenesysApiKey, getBlockedQuotas},
    gsc: {googleAnalytics: {getGMBSetting}},
    siteAuditorV2: {
      loadAuditedSitesList,
      onLoadAuditedSiteDetail,
      getAuditedSiteDetailData,
    },
    navBar: {setShouldToggleSider},
  } = useStore('');
  const [form] = Form.useForm();
  const domainName = getOttoV2Project?.hostname || getSingleUrlParam('domain');
  const uuid = getSingleUrlParam('uuid');
  const [updateOttoProjectLoading, setUpdateOttoProjectLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [showAddPage, setShowAddPage] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [knowledgeGraphSections, setKnowledgeGraphSections] = useState([]);
  const selectedPages = tableData;
  const [tabKeys, setTabKeys] = useState('business_section');
  const formRef = useRef(null);
  const [images, setImages] = useState([]);
  const [youtubeUrl, setYoutubeUrl] = useState([]);
  const [youtubeUrlError, setYoutubeUrlError] = useState(null);
  const [logo, setLogo] = useState(null);
  const [imagesError, setImagesError] = useState('');
  const [knowledgeGraphModalLoader, setKnowledgeGraphModalLoader] = useState(false);

  const [serpLocation, setSerpLocation] = useState();
  const [serpLocationId, setSerpLocationId] = useState();
  const [languageCode, setLanguageCode] = useState<string | null>(null);
  const [callLoadAudit, setCallLoadAudit] = useState(false);
  const isPublicHash = !!getSingleUrlParam('public_hash');
  const [markEmptyFields, setMarkEmptyFields] = useState(false);

  useEffect(() => {
    setDefaultParams({search: '', pageSize: 20, page: 1});
    if (!isPublicHash) {
      getBlockedQuotas();
      getGMBSetting();
      getKnowledgeGraphFieldsMeta();
      getOttoQuotaAllocations();
      getOttoPlanQuotaAllocations();
      loadAuditedSitesList();
    }
    return () => setDefaultParams({...defaultParams, pageSize: 20, page: 1, search: ''}, true);
  }, []);

  const getKnowledgeGraph = async () => {
    if (getOttoV2Project?.knowledgeGraphId && !isPublicHash) {
      await getKnowledgeGraphData(getOttoV2Project?.knowledgeGraphId);
    }
  };

  useEffect(() => {
    getKnowledgeGraph();
  }, [getOttoV2Project?.knowledgeGraphId]);

  useEffect(() => {
    if (getOttoV2Project?.uuid && getOttoV2Project?.uuid == uuid) {
      if (!isPublicHash) getGenesysApiKey();
    }
  }, [getOttoV2Project]);

  const beforeRadius = {
    min: 0,
    max: 0,
  };

  if (getOttoV2Project?.beforeSummary['seoOptimizationScore'] && getOttoV2Project?.beforeSummary['seoOptimizationScore'] !== 'undefined' && getOttoV2Project?.beforeSummary['seoOptimizationScore'] > 50) {
    beforeRadius.max = getOttoV2Project?.beforeSummary['seoOptimizationScore'];
    beforeRadius.min = 100 - getOttoV2Project?.beforeSummary['seoOptimizationScore'];
  } else {
    beforeRadius.min = 100 -getOttoV2Project?.beforeSummary['seoOptimizationScore'];
    beforeRadius.max = getOttoV2Project?.beforeSummary['seoOptimizationScore'];
  }


  const resetForms = () => {
    if (formRef?.current) {
      formRef.current.resetFields();
    }
  };

  const getKnowledgeGraphDataModal = async () => {
    if (getOttoV2Project?.knowledgeGraphId && isKnowledgeModalVisible) {
      form.resetFields();
      setKnowledgeGraphModalLoader(true);
      await getKnowledgeGraphData(getOttoV2Project?.knowledgeGraphId);
      setKnowledgeGraphModalLoader(false);
    }
  };

  useEffect(() => {
    if (!isPublicHash) getKnowledgeGraphDataModal();
  }, [getOttoV2Project?.knowledgeGraphId, isKnowledgeModalVisible]);

  useEffect(() => {
    return () => {
      setShouldToggleSider(true);
    };
  }, []);

  const initialValues = () => {
    resetForms();
    setSearchValue('');
    setSearchResults([]);
    setShowAddPage(false);
    setYoutubeUrlError(null);
    setLogo(null);
  };

  useEffect(() => {
    if (!getOttoV2Project) return;
    const data = getOttoV2Project?.topPages;
    if (getOttoV2Project?.siteAudit && !isPublicHash && (!getAuditedSiteDetailData || getAuditedSiteDetailData?.shouldRepoll || !callLoadAudit)) {
      setCallLoadAudit(true);
      onLoadAuditedSiteDetail(getOttoV2Project?.siteAudit);
    }
    setTableData(data);
  }, [getOttoV2Project, isKnowledgeModalVisible]);

  useEffect(() => {
    if (knowledgeGraph) {
      if (knowledgeGraph['youtubeVideos']) {
        setYoutubeUrl(
          (knowledgeGraph['youtubeVideos'] || []).map((item, index) => ({
            id: index + 1,
            value: item?.url || '',
          })),
        );
      } else {
        setYoutubeUrl([]);
      }

      if (knowledgeGraph['images']) {
        setImages(
          (knowledgeGraph['images'] || []).map((item, index) => ({
            id: index,
            url: item?.url || '',
            types: 'photos',
          })),
        );
      }
      if (knowledgeGraph['languageCode']) {
        setLanguageCode(knowledgeGraph['languageCode']);
      } else {
        setLanguageCode(null);
      }
      setMarkEmptyFields(knowledgeGraph?.['emptyFieldsAreCompleted']);
      setYoutubeUrlError(null);
      if (knowledgeGraph['logo']) {
        setLogo(knowledgeGraph['logo']);
      } else {
        setLogo(null);
      }
      setSerpLocation(knowledgeGraph['serpLocationText']);
    }
  }, [knowledgeGraph, isKnowledgeModalVisible]);

  useEffect(() => {
    if (images?.length > 10) {
      setImagesError('You cannot add more than 10 images.');
    } else {
      setImagesError('');
    }
  }, [images?.length]);


  useEffect(() => {
    const topPages = {name: 'top_pages', label: 'Top pages', value: []};
    const sections = toJS(getOttoKnowledgeGraphFieldsMeta?.value);
    const alreadyExist = sections?.find(item => item.name === 'top_pages');
    if (!alreadyExist) {
      sections?.splice(1, 0, topPages);
    }
    setKnowledgeGraphSections(sections);
  }, [getOttoKnowledgeGraphFieldsMeta]);


  const addProtocolToDomain = (domain: string) => {
    if (!domain) return '';
    return domain?.startsWith('https://') ? domain : `https://${domain}`;
  };

  const onSubmitKnowledgeGraph = async values => {
    if (youtubeUrlError) return;
    if (images?.length > 10) {
      setImagesError('You cannot add more than 10 images.');
      return;
    }
    setUpdateOttoProjectLoading(true);
    try {
      // images tab
      const imagesArr = [];
      for (const item of images) {
        if (item?.url && typeof item?.url !== 'string') {
          const formData = new FormData();
          formData.append('image', item?.url);
          formData.append('format', 'WEBP');
          const response = await ottoGBPUploadImage(formData);
          imagesArr.push({url: response});
        } else {
          imagesArr.push({url: item?.url});
        }
      }
      const youtubeUrls = youtubeUrl.map(item => ({url: item.value}))?.filter(i => i?.url?.length);
      let response;
      if (logo && typeof logo !== 'string') {
        const formData = new FormData();
        formData.append('image', logo);
        formData.append('format', 'PNG');
        response = await ottoGBPUploadImage(formData);
      } else {
        response = logo;
      }
      // images

      // top pages
      const actives = getOttoV2Project?.topPages?.filter(item => item.isSelected);
      if (JSON.stringify(actives) !== JSON.stringify(selectedPages)) {
        const prePayload = {user_top_pages: selectedPages?.filter(i => i?.url?.includes(getSiteName(domainName)))?.map(i => ({url: i?.url, keywords: i?.keywords, is_selected: i?.isSelected}))};
        await updateOttoV2Project(uuid, prePayload);
        loadOttoV2Project(uuid);
      }
      // top pages

      // other all
      Object.entries(values)?.forEach(([key, value]) => {
        if ((key === 'lat' || key === 'long' || key === 'number_of_employees') && value === '') {
          values[key] = null;
        }
        if (key === 'language_code' && !value) {
          delete values[key];
        }
        values.serp_location_text = serpLocation;
        values.serp_location_id = serpLocationId;
        values.images = imagesArr;
        values.youtube_videos = youtubeUrls;
        values.logo = response || '';
      });
      if (languageCode === null || languageCode) values.language_code = languageCode;
      values['empty_fields_are_completed'] = markEmptyFields;
      await updateKnowledgeGraph(getOttoV2Project?.knowledgeGraphId, values);
      // other all
      setUpdateOttoProjectLoading(false);
      setIsKnowledgeModalVisible(false);
      setTabKeys('business_section');
      notification.success('', 'Knowledge Graph updated successfully.');
      // await loadOttoV2Project(uuid);
      await getKnowledgeGraphDataModal();
      initialValues();
    } catch (error) {
      setUpdateOttoProjectLoading(false);
      const errorMessage = customapiError(error) as string;
      notification.error('', errorMessage?.includes('less than or equal to 90') ? 'Latitude: Ensure this value is less than or equal to 90.' : errorMessage);
    }
  };

  const nonFieldTabs = ['images', 'top_pages'];
  return (<>
    <StyledKnowledgeModal
      zIndex={1100}
      visible={isKnowledgeModalVisible}
      onCancel={() => {
        initialValues();
        setTabKeys('business_section');
        setImagesError('');
        setIsKnowledgeModalVisible(false);
      }}
      closable={false}
      footer={false}
      width={750}
    >
      {(getOttoV2Project?.isFrozen && !isPublicHash) ? <FreezeBanner>
        <FontAwesomeIcon icon={faBoxArchive} color='#F1AA3E' />
        <div className='txt' style={{color: 'rgba(0, 0, 0, 0.85)'}}>
             The changes on this site are frozen. You will be unable to make any modifications.
        </div>
      </FreezeBanner> : ''}
      <div className='modal-content'>
        <div className='modal-header' style={{flexDirection: 'column', alignItems: 'baseline'}}>
          <div className='heading'>Knowledge Graph</div>
          <div className='sub-heading'>Ensure all Knowledge Graph information is perfectly accurate.
            Any mistakes here could lead to negative impacts or prevent desired results.</div>
        </div>

        {knowledgeGraphModalLoader ? <RingLoaderV2 style={{margin: '20px 0px'}} height='100px' width='100%' fontSize={50} /> : <Tabs className='modal-tabs' activeKey={tabKeys} onChange={keys => setTabKeys(keys)}>
          {knowledgeGraphSections?.map(item => {
            if (item?.name === 'business_section' && item?.value?.length) {
              item.value = item.value.map(subItem => {
                if (subItem?.name === 'website') {
                  return {...subItem, disabled: true};
                }
                return subItem;
              });
            }
            const allValues = knowledgeGraphSections?.reduce((acc, curr) => acc.concat(curr?.value), []);
            const getInitialValues = () => {
              const obj = {};
              allValues?.forEach(item => {
                obj[item?.name] = item?.name === 'website' ? knowledgeGraph[snakeToCamel(item.name)] ?? addProtocolToDomain(domainName) : knowledgeGraph[snakeToCamel(item.name)];
              });
              return obj;
            };
            return (
              <Tabs.TabPane tab={<StyledTab showErrorIndicator={Boolean(item?.name == 'images' && imagesError)}>{item?.label}</StyledTab>} key={item?.name} disabled={updateOttoProjectLoading}>
                <Form
                  form={form}
                  onFinish={onSubmitKnowledgeGraph}
                  initialValues={getInitialValues()}
                  preserve={false}
                >
                  {
                    item?.name === 'top_pages' ? (
                      <TopPages
                        tableData={tableData}
                        setTableData={setTableData}
                        searchResults={searchResults}
                        setSearchResults={setSearchResults}
                        searchValue={searchValue}
                        setSearchValue={setSearchValue}
                        uuid={uuid}
                        showAddPage={showAddPage}
                        setShowAddPage={setShowAddPage}
                        form={form}
                        domainName={domainName}
                      />
                    ) :
                      item?.name == 'images' ? (
                        <ImagesForm
                          images={images}
                          setImages={setImages}
                          youtubeUrl={youtubeUrl}
                          setYoutubeUrl={setYoutubeUrl}
                          youtubeUrlError={youtubeUrlError}
                          setYoutubeUrlError={setYoutubeUrlError}
                          logo={logo}
                          setLogo={setLogo}
                          maxLimit={10}
                          imagesError={imagesError}
                          hideDragger={false}
                        />
                      ) : (
                        <BusinessInfoForm
                          data={item?.value}
                          setSerpLocation={setSerpLocation}
                          serpLocation={serpLocation}
                          setSerpLocationId={setSerpLocationId}
                          languageCode={languageCode}
                          setLanguageCode={setLanguageCode}
                          form={form}
                          markEmptyFields={markEmptyFields}
                          showFieldsCompletion
                        />
                      )}
                  <StyledModalFooter style={nonFieldTabs?.includes(tabKeys) ? {justifyContent: 'flex-end'} : {}}>
                    {!nonFieldTabs?.includes(tabKeys) && <button type='button' className='mark-fields-button' onClick={() => setMarkEmptyFields(prev => !prev)}>
                      <FontAwesomeIcon icon={faCheck} color='#2D6CCA' fontSize={16} />
                      {markEmptyFields ? 'Unmark' : 'Mark'} empty fields as completed
                    </button>}
                    <div style={{display: 'flex', alignItems: 'center', gap: 20}}>
                      <div className='cancel-btn' onClick={() => {
                        initialValues();
                        form.resetFields();
                        setTabKeys('business_section');
                        setIsKnowledgeModalVisible(false);
                      }}>
                        Cancel
                      </div>
                      <FreezeWrapper>
                        <Tooltip overlayInnerStyle={{minWidth: 275, backgroundColor: '#121212', borderRadius: '12px'}} title={imagesError ? imagesError : ''}>
                          <span>
                            <StyledButton
                              color='green'
                              className='update-button green-btn'
                              htmlType='submit'
                              loading={updateOttoProjectLoading}
                              disabled={imagesError ? true : false}
                            >
                          Update
                            </StyledButton>
                          </span>
                        </Tooltip>
                      </FreezeWrapper>
                    </div>
                  </StyledModalFooter>
                </Form>
              </Tabs.TabPane>
            );
          })}
        </Tabs>}
      </div>
    </StyledKnowledgeModal>
  </>
  );
});

export const StyledTab = styled.div<{showErrorIndicator?: boolean}>`
position: relative;
padding-left: 3px;
  ${p => p?.showErrorIndicator ? `
    padding-right: 6px;
    &::before {
      content: '';
      width: 7px;
      border-radius: 999px;
      height: 7px;
      background-color: #ff4d4f;
      position: absolute;
      top: 3px;
      left: 100%;
    }
  ` : ''}
`;

const StyledButton = styled(Button)<{isSetting?: boolean}>`
  background: ${p => p.isSetting ? '#4E515680': '#2AC15526'}!important;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
  letter-spacing: 0em;
  text-align: left;
  color: #FFFFFF;
  padding: 5px 19px;
  margin: 0px;
  width: fit-content;
  &:hover, &:focus,&:active{
  background: ${p => p.isSetting ? '#676b7280': '#46f17725'}!important;
}
`;


export const KnowledgeButton = styled(Button)`
  background: rgba(52, 174, 243, 0.15);
  border-radius: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
  letter-spacing: 0em;
  text-align: left;
  color: #FFFFFF;
  padding: 5px 19px;
  margin: 0px;
   &:hover, &:focus,&:active{
  background: rgba(52, 174, 243, 0.15) !important;
}
`;

export const StyledKnowledgeModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px !important;
    overflow: hidden !important;
    .ant-modal-body {
      padding: 30px 25px !important;
      .modal-content {
        .modal-header {
          padding: 0px !important;
          .heading {
            font-family: 'Inter', sans-serif;
            font-size: 24px;
            font-weight: 600;
            color: #121212;
          }
          .sub-heading {
            font-size: 16px;
            font-weight: 400;
            color: #4E5156 !important;
            margin-bottom: 20px;
          }
        }
        .modal-tabs {
          .ant-tabs-nav {
            margin: 0 0 8px 0 !important;
            &::before {
              border-bottom: 1px solid #D9D9D9 !important;
            }
            .ant-tabs-nav-wrap {
              .ant-tabs-nav-list {
                .ant-tabs-tab-active {
                  .ant-tabs-tab-btn {
                    font-family: 'Inter', sans-serif;
                    font-size: 16px;
                    font-weight: 500;
                    color: #121212;
                  }
                }
                .ant-tabs-ink-bar {
                  background-color: #2AC155 !important;
                  height: 5px !important;
                  border-radius: 4px 4px 0px 0px !important;
                }
                .ant-tabs-tab {
                  .ant-tabs-tab-btn {
                    font-family: 'Inter', sans-serif;
                    font-size: 16px;
                    font-weight: 400;
                    color: #4E5156 !important;
                  }
                  &:hover {
                    color: #4E5156 !important;
                  }
                }
              }
            }
          }
          .ant-tabs-content-holder {
            .ant-tabs-content {
              .ant-tabs-tabpane {
                .input-wrapper {
                  display: flex;
                  align-items: center;
                  margin-bottom: 12px;
                  .label {
                    font-family: 'Inter', sans-serif;
                    font-size: 12px;
                    font-weight: 400;
                    color: #4E5156;
                  }
                  .ant-col {
                    .text-area {
                      height: 75px;
                      border: 1px solid #D2D2D2;
                      border-radius: 8px;
                      resize: none;
                      &:hover {
                        border: 1px solid #D2D2D2 !important;
                      }
                    }
                    .email-input {
                      height: 38px;
                      border: 1px solid #D2D2D2;
                      border-radius: 8px;
                    }
                    .react-tel-input {
                      .support-phone-input {
                        width: 100% !important;
                        height: 38px !important;
                        border-radius: 8px;

                      }
                      .flag-dropdown {
                        border-radius: 8px 0 0 8px !important;
                      }
                    }  
                  }
                }
              }
            }
          }
        }
      }
    }
    .quota-section {
      display: flex;
      gap: 22px;
    }
  }
`;

export const StyledModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-top: 20px;
  .cancel-btn {
    width: 47px;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #4E5156;
    cursor: pointer;
  }
  .green-btn {
    background-color: #1FAC47 !important;
  }
  .mark-fields-button {
    outline: none;
    border: none;
    background-color: transparent;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    color: #2D6CCA;
    font-size: 12px;
  }
`;


export const FreezeBanner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8.88px;
  margin-bottom: 8px;
  width: 100%;
  height: 38px;
  border-radius: 12px;
  background: #F1AA3E26;
  box-shadow: 0px 3px 2px 0px #00000005;
  padding: 0px 8px;
  .deep-freeze-banner-txt {
    font-family: Inter;
    font-size: 12px;
    font-weight: 400;
    line-height: 14.52px;
    text-align: left;
  }
  .txt {
    font-family: Inter;
    font-size: 14px;
    font-weight: 500;
    line-height: 16.94px;
    text-align: left;
    color: #FFFFFF;
    .unfreeze-button {
      font-family: Inter;
      font-size: 14px;
      font-weight: 500;
      line-height: 16.94px;
      text-align: left;
      text-decoration: underline;
      cursor: pointer;
    }
    .bold-text {
      font-family: Inter;
      font-size: 14px;
      font-weight: 700;
      line-height: 16.94px;
      text-align: left;
      color: #FFFFFF;
    }
  }
`;

